import { jsPDF } from "jspdf";
import { CANCELLATION_FEES, type TierType } from "@/components/portal/TierSelection";
import { FREE_TRIAL_DAYS, FREE_TRIAL_DOMAIN_LIMIT } from "@/lib/trial";
import { PUBLIC_TIER_LABELS } from "../../shared/subscriptionPlans";
import {
  REVIEWED_PRICING_SCHEDULE_TITLE,
  REVIEWED_PRICING_SCHEDULE_VERSION,
  SERVICE_AGREEMENT_LAST_UPDATED_LABEL,
  SERVICE_AGREEMENT_VERSION,
} from "../../shared/serviceAgreement";
export {
  SERVICE_AGREEMENT_ACCEPTED_EVENT_TYPE,
  SERVICE_AGREEMENT_DOWNLOAD_FILE_NAME,
  SERVICE_AGREEMENT_DOWNLOAD_PATH,
  SERVICE_AGREEMENT_LAST_UPDATED_LABEL,
  SERVICE_AGREEMENT_TITLE,
  SERVICE_AGREEMENT_VERSION,
  REVIEWED_PRICING_SCHEDULE_TITLE,
  REVIEWED_PRICING_SCHEDULE_VERSION,
  getServiceAgreementPortalUrl,
} from "../../shared/serviceAgreement";

type DownloadServiceAgreementPdfOptions = {
  clientName?: string | null;
  clientEmail?: string | null;
  acceptedAt?: string | null;
  version?: string | null;
  selectedTier?: TierType | null;
};

type DownloadReviewedPricingSchedulePdfOptions = {
  clientName?: string | null;
  clientEmail?: string | null;
  acceptedAt?: string | null;
  serviceAgreementVersion?: string | null;
  pricingScheduleVersion?: string | null;
  planName?: string | null;
  devPriceCents: number;
  devPriceCurrency?: string | null;
  quantity: number;
  monthlyTotalCents: number;
  chargeTodayCents: number;
  estimatedFirstChargeCents?: number | null;
  trialApplied: boolean;
  trialDays?: number | null;
  billingStartsAt?: string | null;
  minimumActiveDomains?: number | null;
};

const getCancellationFeeText = (selectedTier?: TierType | null) => {
  if (!selectedTier) {
    return "Essential AUD $50 or Premium AUD $99";
  }

  return `AUD $${CANCELLATION_FEES[selectedTier]}`;
};

const formatAcceptedAt = (acceptedAt?: string | null) => {
  if (!acceptedAt) return null;
  const parsed = new Date(acceptedAt);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatCurrencyFromCents = (amountCents: number, currency = "aud") => {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amountCents / 100);
};

const formatDateTimeLabel = (value?: string | null) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const addWrappedText = (
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
) => {
  const lines = doc.splitTextToSize(text, maxWidth);
  doc.text(lines, x, y);
  return y + lines.length * lineHeight;
};

const ensurePageSpace = (doc: jsPDF, y: number, requiredHeight = 20) => {
  if (y + requiredHeight <= 280) return y;
  doc.addPage();
  return 18;
};

export const downloadServiceAgreementPdf = ({
  clientName,
  clientEmail,
  acceptedAt,
  version,
  selectedTier,
}: DownloadServiceAgreementPdfOptions = {}) => {
  const doc = new jsPDF({
    format: "a4",
    unit: "mm",
  });

  const appliedVersion = version || SERVICE_AGREEMENT_VERSION;
  const acceptedLabel = formatAcceptedAt(acceptedAt);
  const cancellationFeeText = getCancellationFeeText(selectedTier);
  const selectedTierLabel = selectedTier ? PUBLIC_TIER_LABELS[selectedTier] : null;
  const margin = 16;
  const maxWidth = 178;
  const lineHeight = 5;
  let y = 18;

  const sections: Array<{ heading: string; body: string[] }> = [
    {
      heading: "1. Authorisation & Access",
      body: [
        "You authorise ShadowScan to access the minimum systems reasonably required to deliver the Services for your Sites, including WordPress administrator access where you provide it, ShadowScan plugin telemetry, hosting control panel access where you provide it, and other access you explicitly approve in writing.",
        "ShadowScan will use approved connector access, tokens, and system information solely to deliver the Services and will not knowingly misuse, share, or exploit that access.",
        "You remain responsible for maintaining valid access and ensuring the Sites remain reachable. If access is unavailable or restricted, some Services may be limited.",
      ],
    },
    {
      heading: "2. Scope of Services",
      body: [
        "ShadowScan provides managed WordPress security designed to reduce security risk, detect suspicious activity early, and respond appropriately. No security service can eliminate all risk.",
        "Services may include continuous monitoring for security signals, safe containment controls where appropriate, alert validation by security staff for high-risk events, portal reporting, hardening recommendations, vulnerability awareness, login abuse detection, and remediation guidance.",
        "ShadowScan may automate or assist with updates where compatible and safe to do so. Updates may be paused or require approval where risk of disruption is identified. You remain responsible for maintaining backups and verifying business-critical functionality after updates unless a separate written arrangement applies.",
      ],
    },
    {
      heading: "3. Plan Responsibilities",
      body: [
        "Essential - Guided Response: automatic protection controls, monitoring, and validated alerts. Where remediation is required, ShadowScan will provide clear guidance and recommended actions. You are responsible for executing remediation actions unless otherwise agreed in writing.",
        "Premium - Managed Response: includes everything in Essential. ShadowScan may perform investigation, containment, and remediation actions on your behalf within reasonable scope using access you provide. ShadowScan may apply emergency stabilisation actions where necessary to reduce immediate risk while prioritising safety and minimising disruption.",
        "Basic Hosting is a separate hosted onboarding offer and does not, by itself, include ShadowScan monitored security or analyst-led response.",
      ],
    },
    {
      heading: "4. Fees, Billing, and Subscription",
      body: [
        "Services are billed per domain on a month-to-month subscription basis. Billing occurs on the 1st of each month for all active domains. Added domains are included under this Agreement and billed accordingly.",
        "Published self-serve managed security pricing currently includes Essential AUD $50 per domain/month and Premium AUD $185 per domain/month.",
        "Basic Hosting is a separate customer-facing hosting and onboarding offer. It is not a managed security tier under this Agreement unless expressly stated otherwise in writing.",
        "Approved reviewed pricing paths do not use the published self-serve fee table. The exact custom rate, quantity, and trial or billing-start terms for a reviewed pricing activation are shown and accepted separately in the portal reviewed pricing schedule.",
        "The reviewed pricing schedule forms part of the accepted billing terms for that reviewed pricing activation.",
        `Eligible self-serve plans may include a ${FREE_TRIAL_DAYS}-day free trial for up to ${FREE_TRIAL_DOMAIN_LIMIT} domains, with billing starting only after the trial ends.`,
        `Approved reviewed pricing paths may also begin with the same ${FREE_TRIAL_DAYS}-day trial when offered in the portal, with billing starting after the trial ends.`,
        "A payment method is required before a trial starts. Payment is required to maintain active service after the free trial or for non-trial activation paths. If payment is not received, ShadowScan may suspend or limit Services until billing resumes.",
        `For self-serve plans, cancellation or removal of a domain incurs a fee of ${cancellationFeeText} per domain, unless the domain is removed before the end of an applicable ${FREE_TRIAL_DAYS}-day free trial.`,
        "Reviewed pricing paths follow the cancellation, refund, and reassessment terms shown in the accepted reviewed pricing schedule for that activation.",
        "Unless otherwise stated in writing or required by law, fees already charged are non-refundable and partial-month refunds do not apply for cancelled or removed domains.",
      ],
    },
    {
      heading: "5. Cancellation & Termination",
      body: [
        "You may cancel services or remove individual domains at any time.",
        `For self-serve plans, cancellation or removal of a domain incurs a ${cancellationFeeText} cancellation fee per domain.`,
        `No cancellation or removal fee applies if an eligible domain is removed before the end of the applicable ${FREE_TRIAL_DAYS}-day free trial.`,
        "No partial-month refunds apply for cancelled or removed domains unless required by law or expressly stated otherwise in writing.",
        "Reviewed pricing paths follow the cancellation, refund, and reassessment terms shown in the accepted reviewed pricing schedule for that activation.",
        "Where reviewed pricing has been approved based on agreed portfolio size, domain count, spend level, or similar commercial assumptions, ShadowScan may review that pricing if those assumptions materially change.",
        "Any resulting pricing change following that review will apply prospectively from a future billing cycle unless otherwise agreed in writing.",
        "ShadowScan may suspend or terminate Services for non-payment or material breach of this Agreement.",
      ],
    },
    {
      heading: "6. Client Responsibilities",
      body: [
        "Maintain current backups for your Sites, maintain lawful authority to request security changes, keep WordPress, plugins, and themes reasonably supported or accept the risk of unsupported components, provide timely access and approvals required to deliver Services, and use the ShadowScan Portal and Plugin in accordance with reasonable security practices.",
      ],
    },
    {
      heading: "7. Limitations and No Guarantees",
      body: [
        "ShadowScan uses reasonable efforts, tools, and processes intended to reduce security risk. However, cyber threats evolve and no service can ensure avoidance of all incidents.",
        "ShadowScan is not responsible for losses arising from third-party hosting outages or misconfigurations outside ShadowScan control, compromised credentials not provided or managed by ShadowScan, unsupported or pirated software, or actions taken by you or third parties that reduce security.",
      ],
    },
    {
      heading: "8. Limitation of Liability",
      body: [
        "To the extent permitted by law, ShadowScan's liability is limited to the fees paid for the affected Sites in the preceding 30 days. ShadowScan is not liable for indirect or consequential loss except where liability cannot be excluded under applicable law.",
      ],
    },
    {
      heading: "9. Confidentiality",
      body: [
        "Each party agrees to keep confidential any non-public information received from the other party in connection with the Services, except as required to deliver the Services or as required by law.",
      ],
    },
    {
      heading: "10. Governing Law",
      body: [
        "This Agreement is governed by the laws of New South Wales, Australia. Each party submits to the non-exclusive jurisdiction of the courts of New South Wales and the Commonwealth courts competent to hear appeals from those courts.",
      ],
    },
    {
      heading: "11. Entire Agreement",
      body: [
        "This Agreement, the accepted reviewed pricing schedule (if any), the ShadowScan Privacy Policy, and the ShadowScan Plugin Addendum (if you use the plugin) form the relevant customer-facing document set for the Services.",
        "If there is an inconsistency, the reviewed pricing schedule controls for pricing, the Plugin Addendum controls for plugin-specific behaviour, and this Agreement controls for managed service delivery unless the law requires otherwise.",
      ],
    },
  ];

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("ShadowScan Security Services Agreement", margin, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  y = addWrappedText(
    doc,
    `Version ${appliedVersion} · Last updated ${SERVICE_AGREEMENT_LAST_UPDATED_LABEL}`,
    margin,
    y,
    maxWidth,
    4,
  );
  y += 2;

  if (clientName || clientEmail || acceptedLabel) {
    y = ensurePageSpace(doc, y, 24);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Acceptance details", margin, y);
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    if (clientName) {
      y = addWrappedText(doc, `Client: ${clientName}`, margin, y, maxWidth, lineHeight);
    }
    if (clientEmail) {
      y = addWrappedText(doc, `Contact email: ${clientEmail}`, margin, y, maxWidth, lineHeight);
    }
    if (acceptedLabel) {
      y = addWrappedText(doc, `Accepted at: ${acceptedLabel}`, margin, y, maxWidth, lineHeight);
    }
    if (selectedTierLabel) {
      y = addWrappedText(doc, `Selected plan: ${selectedTierLabel}`, margin, y, maxWidth, lineHeight);
    }
    y += 2;
  }

  y = ensurePageSpace(doc, y, 16);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  y = addWrappedText(
    doc,
    "This Agreement is between Valnex Holdings trading as ShadowScan and the Client for the provision of managed WordPress security services for the website domains added to the ShadowScan Portal.",
    margin,
    y,
    maxWidth,
    lineHeight,
  );
  y += 2;
  y = addWrappedText(
    doc,
    "If you use the ShadowScan WordPress plugin as part of those Services, the ShadowScan Plugin Addendum at https://shadowscan.com.au/plugin-addendum is incorporated into this Agreement and controls for plugin-specific behaviour where it is more specific.",
    margin,
    y,
    maxWidth,
    lineHeight,
  );
  y += 4;

  for (const section of sections) {
    y = ensurePageSpace(doc, y, 18);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(section.heading, margin, y);
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    for (const paragraph of section.body) {
      y = ensurePageSpace(doc, y, 16);
      y = addWrappedText(doc, `• ${paragraph}`, margin, y, maxWidth, lineHeight);
      y += 2;
    }
  }

  y = ensurePageSpace(doc, y, 18);
  doc.setFont("helvetica", "bold");
  doc.text("Service Provider", margin, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  addWrappedText(
    doc,
    "Valnex Holdings trading as ShadowScan · shadowscan.com.au · support@shadowscan.com.au",
    margin,
    y,
    maxWidth,
    lineHeight,
  );

  doc.save(`shadowscan-service-agreement-${appliedVersion}.pdf`);
};

export const downloadReviewedPricingSchedulePdf = ({
  clientName,
  clientEmail,
  acceptedAt,
  serviceAgreementVersion,
  pricingScheduleVersion,
  planName,
  devPriceCents,
  devPriceCurrency,
  quantity,
  monthlyTotalCents,
  chargeTodayCents,
  estimatedFirstChargeCents,
  trialApplied,
  trialDays,
  billingStartsAt,
  minimumActiveDomains,
}: DownloadReviewedPricingSchedulePdfOptions) => {
  const doc = new jsPDF({
    format: "a4",
    unit: "mm",
  });

  const appliedAgreementVersion = serviceAgreementVersion || SERVICE_AGREEMENT_VERSION;
  const appliedScheduleVersion = pricingScheduleVersion || REVIEWED_PRICING_SCHEDULE_VERSION;
  const acceptedLabel = formatAcceptedAt(acceptedAt);
  const billingStartsLabel = formatDateTimeLabel(billingStartsAt);
  const currency = devPriceCurrency || "aud";
  const margin = 16;
  const maxWidth = 178;
  const lineHeight = 5;
  let y = 18;

  const rows = [
    ["Plan", planName || "Dev"],
    ["Monthly fee per site", formatCurrencyFromCents(devPriceCents, currency)],
    ["Quantity accepted", String(quantity)],
    ["Monthly total", formatCurrencyFromCents(monthlyTotalCents, currency)],
    ["Charge today", formatCurrencyFromCents(chargeTodayCents, currency)],
    ["Cancellation fee on removal", "No cancellation fee applies under this reviewed pricing schedule"],
    ["Refunds on removal", "No partial-month refund is issued for the current billing period"],
    ["Future billing after removal", "Stops from the next billing cycle after removal"],
    ["Minimum active domains to retain reviewed pricing", minimumActiveDomains ? String(minimumActiveDomains) : "Not specified"],
    ["Trial applied", trialApplied ? `Yes${trialDays ? ` (${trialDays} days)` : ""}` : "No"],
    ["Estimated first charge after trial", trialApplied && typeof estimatedFirstChargeCents === "number"
      ? formatCurrencyFromCents(estimatedFirstChargeCents, currency)
      : "Not applicable"],
    ["Billing cadence", "1st of each month"],
    ["Billing starts", billingStartsLabel || "As shown in the portal"],
  ] as const;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(REVIEWED_PRICING_SCHEDULE_TITLE, margin, y);
  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  y = addWrappedText(
    doc,
    `Pricing schedule version ${appliedScheduleVersion} · Linked service agreement version ${appliedAgreementVersion}`,
    margin,
    y,
    maxWidth,
    4,
  );
  y += 2;

  if (clientName || clientEmail || acceptedLabel) {
    y = ensurePageSpace(doc, y, 24);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Acceptance details", margin, y);
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    if (clientName) {
      y = addWrappedText(doc, `Client: ${clientName}`, margin, y, maxWidth, lineHeight);
    }
    if (clientEmail) {
      y = addWrappedText(doc, `Contact email: ${clientEmail}`, margin, y, maxWidth, lineHeight);
    }
    if (acceptedLabel) {
      y = addWrappedText(doc, `Accepted at: ${acceptedLabel}`, margin, y, maxWidth, lineHeight);
    }
    y += 2;
  }

  y = ensurePageSpace(doc, y, 18);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  y = addWrappedText(
    doc,
    "This reviewed pricing schedule forms part of the accepted billing terms for this activation under the ShadowScan Security Services Agreement. It records the exact reviewed pricing, removal terms, and any minimum active-domain reassessment threshold shown in the portal when you accepted it.",
    margin,
    y,
    maxWidth,
    lineHeight,
  );
  y += 4;

  y = ensurePageSpace(doc, y, 18);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Accepted reviewed pricing", margin, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  for (const [label, value] of rows) {
    y = ensurePageSpace(doc, y, 12);
    y = addWrappedText(doc, `${label}: ${value}`, margin, y, maxWidth, lineHeight);
    y += 1;
  }

  y += 4;
  y = ensurePageSpace(doc, y, 18);
  doc.setFont("helvetica", "bold");
  doc.text("Service Provider", margin, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  addWrappedText(
    doc,
    "Valnex Holdings trading as ShadowScan · shadowscan.com.au · support@shadowscan.com.au",
    margin,
    y,
    maxWidth,
    lineHeight,
  );

  doc.save(`shadowscan-reviewed-pricing-schedule-${appliedScheduleVersion}.pdf`);
};
